"use client"

import { useState, useEffect } from "react"
import { apiClient } from "../services/apiClient"
import { useLanguage } from "../contexts/LanguageContext"
import { Icons } from "./Icons"
import { TrainingRouter } from "./TrainingRouter"

export function TeacherDashboard() {
  const { t, language } = useLanguage()
  const [students, setStudents] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState("")
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false)
  const [showTrainings, setShowTrainings] = useState(false)

  useEffect(() => {
    loadStudents()
  }, [])

  const loadStudents = async () => {
    try {
      const response = await apiClient.getStudents()
      setStudents(response.students)
    } catch (err) {
      setError(err.message || "Error loading students")
    } finally {
      setIsLoading(false)
    }
  }

  const blockStudent = async (studentId) => {
    try {
      await apiClient.blockStudent(studentId)
      await loadStudents()
    } catch (err) {
      setError(err.message || "Error blocking student")
    }
  }

  const unblockStudent = async (studentId) => {
    try {
      await apiClient.unblockStudent(studentId)
      await loadStudents()
    } catch (err) {
      setError(err.message || "Error unblocking student")
    }
  }

  const deleteStudent = async (studentId, studentName) => {
    if (!window.confirm(t("common.deleteConfirmText"))) {
      return
    }

    try {
      await apiClient.deleteStudent(studentId)
      setError("")
      alert(t("messages.userDeleted"))
      await loadStudents()
    } catch (err) {
      setError(err.message || t("messages.deleteError"))
    }
  }

  const resetStudentPassword = async (studentId, studentName) => {
    if (!window.confirm(`${t("common.resetPassword")} для ${studentName}?`)) {
      return
    }

    try {
      const response = await apiClient.resetStudentPassword(studentId, language)
      setError("")
      alert(response.message || t("messages.passwordReset"))
    } catch (err) {
      setError(err.message || t("messages.resetError"))
    }
  }

  const stats = {
    total: students.length,
    active: students.filter((s) => s.is_active).length,
    blocked: students.filter((s) => !s.is_active).length,
  }

  if (isLoading) {
    return (
      <div className="text-center p-8">
        <Icons.Spinner className="w-8 h-8 mx-auto mb-4" />
        <p>{t("common.loading")}</p>
      </div>
    )
  }


  if (showTrainings) {
    return <TrainingRouter onBack={() => setShowTrainings(false)} showAddons={true} />
  }

  return (
    <div className="space-y-6">

      {/* Быстрые действия */}
      <div className="grid grid-cols-1 gap-4">
        <div className="card">
          <div className="card-content btn" onClick={() => setShowTrainings(true)}>
            <div className="text-center p-6">
              <Icons.Book className="w-8 h-8 mx-auto mb-4 text-blue-600" />
              <h3 className="font-semibold">{t("student.courses")}</h3>
              <p className="text-sm text-gray-600">{t("student.coursesDesc")}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold flex items-center space-x-3">
          <Icons.Teacher className="w-8 h-8 text-green-600" />
          <span>{t("teacher.title")}</span>
        </h1>
        <button className="btn btn-primary flex items-center space-x-2" onClick={() => setIsInviteModalOpen(true)}>
          <Icons.Plus className="w-4 h-4" />
          <span>{t("teacher.inviteStudent")}</span>
        </button>
      </div>

      {/* Статистика */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.Users className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <p className="text-sm text-gray-600">{t("common.total")}</p>
              <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.CheckCircle className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <p className="text-sm text-gray-600">{t("common.active")}</p>
              <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-content">
            <div className="text-center">
              <Icons.XCircle className="w-6 h-6 mx-auto mb-2 text-red-600" />
              <p className="text-sm text-gray-600">{t("common.blocked")}</p>
              <p className="text-2xl font-bold text-red-600">{stats.blocked}</p>
            </div>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Список студентов */}
      <div className="card">
        <div className="card-header">
          <h2 className="card-title flex items-center space-x-2">
            <Icons.Student className="w-5 h-5 text-blue-600" />
            <span>{t("teacher.myStudents")}</span>
          </h2>
          <p className="card-description">{t("teacher.studentManagement")}</p>
        </div>
        <div className="card-content">
          {students.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Icons.Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg mb-2">{t("teacher.noStudents")}</p>
              <p className="text-sm">{t("teacher.inviteFirst")}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {students.map((student) => (
                <div key={student.id} className="justify-between items-center p-4 border border-rounded">
                  <div className="grid grid-cols-4 space-x-4">
                    <div className="flex space-x-2">
                      <div>
                        <Icons.Student className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium">{student.full_name}</p>
                        <p className="text-sm text-gray-600">{student.email}</p>
                        <p className="text-xs text-gray-500">
                          {t("teacher.addedOn")} {new Date(student.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div>
                    <span
                      className={`badge ${student.is_active ? "badge-success" : "badge-danger"} flex items-center space-x-1`}
                    >
                      {student.is_active ? (
                        <Icons.CheckCircle className="w-3 h-3" />
                      ) : (
                        <Icons.XCircle className="w-3 h-3" />
                      )}
                      <span>{student.is_active ? t("common.active") : t("common.blocked")}</span>
                    </span>
                    </div>
                    <div>
                    <span className="badge badge-sm">
                      {t("nav.visits")}:{` `}
                      {`${student.visits}`}<br />
                      {t("nav.last_entry")}:<br />
                      {`${student.last_session && new Date(student.last_session).toLocaleString(language) || t("nav.never")}`}
                    </span>
                    </div>
                  <div className="grid gap-4">
                    {student.is_active ? (
                      <button
                        className="btn btn-sm btn-danger flex items-center space-x-1"
                        onClick={() => blockStudent(student.id)}
                      >
                        <Icons.Lock className="w-3 h-3" />
                        <span>{t("admin.block")}</span>
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-primary flex items-center space-x-1"
                        onClick={() => unblockStudent(student.id)}
                      >
                        <Icons.Unlock className="w-3 h-3" />
                        <span>{t("admin.unblock")}</span>
                      </button>
                    )}
                    <button
                      className="btn btn-sm btn-secondary flex items-center space-x-1"
                      onClick={() => resetStudentPassword(student.id, student.full_name)}
                    >
                      <Icons.Key className="w-3 h-3" />
                      <span>{t("common.resetPassword")}</span>
                    </button>
                    <button
                      className="btn btn-sm btn-danger flex items-center space-x-1"
                      onClick={() => deleteStudent(student.id, student.full_name)}
                    >
                      <Icons.Delete className="w-3 h-3" />
                      <span>{t("common.deleteUser")}</span>
                    </button>
                  </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Модальное окно приглашения студента */}
      {isInviteModalOpen && (
        <InviteStudentModal
          onClose={() => setIsInviteModalOpen(false)}
          onSuccess={() => {
            setIsInviteModalOpen(false)
            loadStudents()
          }}
        />
      )}
    </div>
  )
}

function InviteStudentModal({ onClose, onSuccess }) {
  const { t, language } = useLanguage()
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")
    setIsLoading(true)

    try {
      const response = await apiClient.inviteStudent(email, fullName, language)
      setSuccess(response.message || t("inviteStudent.success"))
      setEmail("")
      setFullName("")

      setTimeout(() => {
        onSuccess()
      }, 1500)
    } catch (err) {
      setError(err.message || t("inviteStudent.error"))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <h2 className="text-xl font-semibold mb-4 flex items-center space-x-2">
          <Icons.Student className="w-6 h-6 text-blue-600" />
          <span>{t("inviteStudent.title")}</span>
        </h2>
        <p className="text-gray-600 mb-6">{t("inviteStudent.description")}</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label" htmlFor="email">
              {t("auth.email")}
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("inviteStudent.emailPlaceholder")}
              required
            />
          </div>
          <div>
            <label className="label" htmlFor="fullName">
              {t("inviteStudent.fullName")}
            </label>
            <input
              id="fullName"
              className="input"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t("inviteStudent.fullNamePlaceholder")}
              required
            />
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          {success && <div className="alert alert-success">{success}</div>}
          <div className="flex space-x-2">
            <button
              type="submit"
              className="btn btn-primary flex-1 flex items-center justify-center space-x-2"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Icons.Spinner className="w-4 h-4" />
                  <span>{t("inviteStudent.sending")}</span>
                </>
              ) : (
                <>
                  <Icons.Plus className="w-4 h-4" />
                  <span>{t("teacher.inviteStudent")}</span>
                </>
              )}
            </button>
            <button type="button" className="btn btn-outline" onClick={onClose}>
              {t("common.cancel")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
